/**
 * @lines98/client/store/game-store — Zustand store: live game + moves history.
 *
 * Per ADR-0001 §A2: Zustand ~3 KB. Single store; no `devtools`/`persist`
 * middleware. localStorage persistence is bespoke (see `services/replay-store.ts`).
 *
 * SCOPE:
 *   - `engineState` — live `GameState` from `@lines98/shared/engine` (canonical).
 *     The engine state is OPAQUE to the store — we never reach into `engineState.grid`
 *     from outside; UI reads `getGrid()` / `getNext()` selectors instead.
 *   - `moves` — accepted `Move[]` history (recorded as `applyMove` returns ok).
 *   - `selectedCell` — UI selection on the board.
 *   - `phase` — current screen ('board' | 'result' | 'replay').
 *   - `finishReason` — once endGame fires, the «why» for the result screen.
 *
 * INVARIANTS (load-bearing, do not break in subsequent commits):
 *   - `score === replayScore(seed, moves).score` always holds after any commit
 *     of an action that doesn't push to `moves`. Tested in `tests/store/game-store.test.ts`.
 *   - The store imports ONLY from `@lines98/shared` for engine logic. No local
 *     `applyMove` / `replayScore` re-implementation (AC-4).
 *   - All numeric constants (N, COLORS, MIN_LINE_LENGTH, CURRENT_ENGINE_VERSION)
 *     come from `@lines98/shared/config/balance.ts`. No magic numbers.
 *
 * Per replays-ghosts.md §3.1: replay uses the same engine as live play → the
 * `moves` array is the canonical source for verification. `score` in this store
 * is the *client-side* mirror; in T-5 there's no server, so it's authoritative
 * for the local-only result screen.
 */

import { createStore, type StoreApi } from 'zustand/vanilla';

import { applyMove, startState, UINT32_MAX } from '@lines98/shared';
import {
  CURRENT_ENGINE_VERSION,
  N,
} from '@lines98/shared/config/balance';
import { DEFAULT_CONTENT_LANG, type UiLang } from '@lines98/shared/config/seo';
import type {
  CellValue,
  ColorIndex,
  FinishReason,
  GameMode,
  GameState,
  Move,
  MoveEvents,
  MoveResult,
  NextBall,
} from '@lines98/shared/types/game';

import type { SubmitResult } from '../services/game-submit.js';

// =============================================================================
// Phases — drives which screen is mounted by `app.ts`
// =============================================================================

export type Phase =
  // Стартовая страница (паттерн Minesweeper Online, owner-directive 2026-06-17):
  // минимальный лендинг-surface на корне `/` — логотип-герой + 3 карточки-режима
  // (casual) + крошечный SEO-хук. Дефолт-фаза boot голого `/`. Не доска и не лобби.
  | 'start'
  | 'daily'
  | 'modeSelect'
  | 'board'
  | 'result'
  | 'replay'
  | 'leaderboard'
  | 'settings'
  // 'workshop' фаза УДАЛЕНА вместе с «животными» (workshop-animals-removal.md):
  // экран зверей снят, пункт рейла теперь disabled. Прямой /workshop → graceful
  // fallback (lib/game-routing isRetiredGamePath → дефолт-поверхность).
  | 'gnomes'
  | 'profile'
  // «Книга реплеев» — личный каталог реплеев (ADR-0023 / replay-collection.md):
  // account-bound surface (Свои авто-∞ / Чужие ручные с ёмкостью + вкладки режимов).
  | 'book'
  // Страница «Поддержать» (крипто-донат L0, ADR-0024 addendum). Game-agnostic
  // meta-surface: 3 крипто-карты приёма (USDT/Ethereum/Bitcoin) из lib/donate-targets.
  | 'support'
  // Контент-страница (лендинг / правила / гайд / стратегия / FAQ / about) ВНУТРИ
  // приложения — тот же контент, что краулер видит в статике (ADR-0020 §«Сшить
  // контент в приложение»). Цель = `contentTarget` (lang + slug).
  | 'content';

export interface SelectedCell {
  readonly r: number;
  readonly c: number;
}

// =============================================================================
// Game context — daily vs casual (T-7)
// =============================================================================

/**
 * Discriminated union: identifies whether the current play session is а daily-board
 * attempt (server-seeded, one-per-day, mode locked к `predict`) или а casual local
 * play (random seed, mode free, no server submission).
 *
 * Per `core-daily-board.md` §2.1 + §3.4: daily fixes mode к `predict` и uses the
 * server-returned seed. Casual is the existing local-play surface from T-5.
 *
 * Per dispatch_t-7 §2 C2: extends game-store без replacing the existing seed/mode
 * fields — those remain authoritative для the live engine; context decides ONLY
 * the policy boundaries (server submit vs local replay-store, mode switcher
 * visibility, daily-cache key, result-screen branch).
 */
export type GameContext =
  | { readonly kind: 'daily'; readonly dateOfDay: string; readonly seedFromServer: number }
  | {
      /**
       * Non-daily server-scored game (extra-modes, ADR-0013). Server-issued
       * `sessionId` + `seedFromServer` from `POST /game/start`; the mode is the
       * player-chosen one (controls `next`-visibility only — §2.1). Scored to the
       * all-time leaderboard via `POST /finish-game` with
       * `context = { kind:'all_time', session_id }`. No one-per-day limit (AC-3).
       */
      readonly kind: 'all_time';
      readonly sessionId: string;
      readonly seedFromServer: number;
    }
  | { readonly kind: 'casual' };

/** Convenience constructor для casual default (used везде где «новая локальная игра»). */
export const CASUAL_CONTEXT: GameContext = { kind: 'casual' } as const;

// =============================================================================
// Store shape
// =============================================================================

export interface GameStoreState {
  /** Current screen — drives mounting by `app.ts`. */
  phase: Phase;
  /** Live engine state. OPAQUE to UI — access through selectors below. */
  engineState: GameState | null;
  /** Game mode (T-5: 'predict' для local play; T-7: locked к 'predict' для daily). */
  mode: GameMode;
  /** Seed of the current session (uint32). */
  seed: number;
  /** Recorded accepted moves. */
  moves: Move[];
  /**
   * Current session context — `casual` (existing T-5 local play) or `daily`
   * (T-7 server-seeded daily-board attempt). Drives UI policy (mode switcher
   * hidden, daily-cache writes, server submit на end).
   */
  context: GameContext;
  /** Currently-selected cell (a ball waiting for а target). */
  selectedCell: SelectedCell | null;
  /** Once endGame fires — the cause; null while in-progress. */
  finishReason: FinishReason | null;
  /**
   * Daily-only: server submit outcome (set после endGame + POST /finish-game).
   * Null for casual games or before submit response. Drives result-screen
   * banner (verified / duplicate / rejected / network).
   */
  dailySubmitResult: SubmitResult | null;
  /** Engine version stamped on this session (for future audit / replay context). */
  readonly engineVersion: string;
  /** Last invalid-move reason (for UI feedback like shake / toast). */
  lastInvalidReason: 'occupied' | 'nopath' | null;
  /**
   * Per-move clear events of the LAST accepted move (board-feedback-animations.md
   * §2.3). Transient UI hint — drives the line-clear overlay animation; not
   * persisted, not in the replay. `null` when the last move cleared nothing (or
   * before any move). board-screen reads it to animate the cleared line.
   */
  lastMoveEvents: MoveEvents | null;
  /**
   * Transient «recovery replay in progress» flag (board-feedback-animations.md
   * §2.4 / AC-13). Set `true` while `daily-board-screen.resumeInProgress` re-plays
   * the cached moves of a refreshed daily attempt — those moves rebuild the engine
   * state under the live board-screen subscription, so without this guard each
   * replayed move would fire the spawn-pop appearance animation AND the line-clear
   * celebration overlay (a partial daily game would «replay» its pops/celebrations
   * on recovery). While set, `renderBoardDiff` paints STATICALLY (no spawn-pop, no
   * line-clear overlays). Reset to `false` once recovery finishes → normal play
   * animates as usual. Not persisted, not in the replay.
   */
  replaying: boolean;
  /**
   * When non-null, the `replay` phase mounts a REMOTE replay (fetched by
   * `short_id`) instead of the own last-game. Set by `showReplayById`, cleared by
   * `showReplay` / fresh starts. Drives the app.ts `replay` mount branch.
   */
  replayShortId: string | null;
  /**
   * When non-null, the `leaderboard` phase mounts the ALL-TIME leaderboard on
   * this mode tab (extra-modes §5.4); when null, the daily-board leaderboard.
   * Set by `showLeaderboard(mode?)`. Drives the app.ts `leaderboard` mount branch.
   */
  leaderboardAllTimeMode: GameMode | null;

  /**
   * When non-null, the `gnomes` phase mounts «Помощники» FOCUSED on this gnome
   * colour: the screen scrolls its target card into the bounded `.gnomes-scroll`
   * band and applies a transient highlight. Set by `showGnomes(focusColor?)`
   * (mirror of `leaderboardAllTimeMode` / `showLeaderboard(mode?)`); CONSUMED ONCE
   * — the screen clears it to null after applying focus so a re-mount (locale
   * change) does NOT re-focus a stale target. Drives the app.ts `gnomes` branch.
   */
  gnomesFocusColor: ColorIndex | null;

  /**
   * Target content page for the `content` phase (ADR-0020 §«Сшить контент в
   * приложение»): `{ lang, slug }` (slug '' = лендинг). Set by `showContent`,
   * read by the app.ts `content` mount branch. `null` когда не на контент-фазе.
   * Origin-relative routing: app.ts строит/читает путь через `@lines98/shared
   * /config/content-routes` (тот же источник, что SSG) → URL совпадает с краулером.
   */
  contentTarget: { lang: string; slug: string } | null;

  /**
   * Active UI language — THE single source-of-truth for the whole product (ADR-0021
   * D-2, T-I18N-2). Drives the game UI text + content pages + the rail language
   * switcher simultaneously. `contentTarget.lang` is DERIVED from this (synced by
   * `setLocale` while on the content phase). One of `UI_LANGS` (12). Geo-default +
   * persistence (localStorage / `players.locale`) is T-I18N-3 — here the start value
   * is just the `en` fallback (`DEFAULT_CONTENT_LANG`); not a magic literal.
   */
  locale: UiLang;

  /**
   * Pre-start PREVIEW flag for the non-daily game screen (run-persistence-lifecycle.md
   * T-A / addendum-001 Решение 1 — lazy-on-preview). While `true`, the board screen
   * shows the starting layout of an ALREADY-ALLOCATED server session at the selected
   * `mode`'s hint level — this is the LITERAL scored partition (preview = зачётная
   * партия, I-1), NOT a throwaway client preview. The context is `all_time` (server
   * `session_id` + server seed from `POST /game/start`, fired on ENTERING the preview,
   * not on «Начать»). The score/moves line MORPHS into «Начать» + «перебросить старт»
   * (RP-2). «Начать» = a pure client `preStart→false` over the loaded session — the
   * seed does NOT change, the board is NOT reshuffled (AC-A.2). «Перебросить старт» =
   * a fresh `POST /game/start` (new session, new layout — AC-A.3). Reset to `false`
   * once the scored game begins (`beginPreviewedGame`) or the player leaves. Not
   * persisted. Anti-shadow (I-2): the daily-seed is NEVER revealed here — non-daily
   * seed is unique per partition (addendum «Граница»).
   */
  preStart: boolean;

  // ---- actions ----

  /**
   * Start а new casual game with given seed (or new random uint32). Resets
   * context to `casual`. Daily attempts use `startDailyGame()` instead.
   *
   * `mode` (optional, default `predict`) — only the casual RESUME path passes a
   * non-predict mode (restoring a cached casual run at its original hint level —
   * RP-4). All other callers omit it (predict casual).
   */
  startNewGame(seed?: number, mode?: GameMode): void;

  /**
   * Start а daily-board attempt with the server-returned seed + dateOfDay.
   *
   * Per `core-daily-board.md` §2.1: mode is locked к `predict`. Per §3.2: seed
   * comes from server (`GET /daily/seed`), client does NOT derive it. The store
   * keeps the daily context on `endGame()` для downstream submit flow.
   */
  startDailyGame(args: { seed: number; dateOfDay: string }): void;

  /**
   * Mount a non-daily server-scored game (extra-modes, ADR-0013). The seed +
   * sessionId come from `POST /game/start { mode }`; `mode` controls only
   * `next`-visibility (§2.1). On `endGame()` the game is submitted with
   * `context = { kind:'all_time', session_id }` and scored to the all-time
   * leaderboard. No one-per-day limit (AC-3) — «Играть ещё» takes a fresh session.
   *
   * `preStart` (run-persistence-lifecycle.md T-A / addendum-001 Решение 1):
   *   - `true`  — lazy-on-preview: the session was just allocated on ENTERING the
   *     preview; the board renders its starting layout but moves are not yet scored.
   *     «Начать» later flips `preStart→false` via {@link beginPreviewedGame} WITHOUT
   *     a second `POST /game/start` (the seed/board never reshuffles — AC-A.2).
   *   - `false` (default) — the game is immediately live (legacy direct-start path,
   *     e.g. «Играть ещё» / re-roll that begins play right away). The caller already
   *     decided to start scoring.
   */
  startAllTimeGame(args: {
    seed: number;
    sessionId: string;
    mode: GameMode;
    preStart?: boolean;
  }): void;

  /**
   * Begin the previewed all-time game (run-persistence-lifecycle.md T-A, «Начать»).
   * A pure client transition `preStart→false` over the ALREADY-LOADED server session
   * — the seed does NOT change, the board is NOT reshuffled, NO network request fires
   * (the session was allocated on entering the preview — addendum Решение 1 / AC-A.2).
   * No-op when not in preview (`preStart === false`). After this, moves are scored.
   */
  beginPreviewedGame(): void;

  /** Select / deselect a cell. Click on занятую клетку — select; на пустую — attemptMove. */
  selectCell(r: number, c: number): void;

  /**
   * Try to move from `selectedCell` to `(r, c)`. Returns the engine MoveResult.
   * If `ok`, the move is pushed to `moves[]`. If !ok, `lastInvalidReason` is set.
   *
   * Self-move (sr=r, sc=c) — handled like a no-op «click on selected ball again»:
   * the engine returns `{ok:false, reason:'occupied'}` because target is non-empty;
   * UI should treat this as a re-selection / dismiss (see `cellClickHandler`).
   */
  attemptMove(r: number, c: number): MoveResult;

  /** End the current game with given reason. Locks the board for the result screen. */
  endGame(reason: FinishReason): void;

  /** Transition to the result screen (called after endGame). */
  showResult(): void;

  /** Transition to the replay screen for the OWN just-finished game (last-game). */
  showReplay(): void;

  /**
   * Transition to the replay screen for a REMOTE replay identified by `shortId`
   * (in-shell nav from the leaderboard). The `replay` phase mount fetches
   * `GET /api/replay/<shortId>` and branches to player (PLAYABLE) / teaser
   * (TEASER) / not-found STRICTLY per the server `kind` (replays-ghosts.md §5.5 —
   * client never decides the gate). Own-replay path = `replayShortId === null`.
   */
  showReplayById(shortId: string): void;

  /**
   * Transition to the START page (минимальный лендинг — паттерн Minesweeper Online,
   * owner-directive 2026-06-17). Голый корень `/` boot'ит сюда; бренд-логотип рейла
   * (`onBrandSelect`) возвращает сюда. Surface = логотип-герой + 3 карточки casual +
   * крошечный SEO-хук; никакой партии/доски не запускает (минимализм).
   */
  showStart(): void;

  /** Transition to the daily-board screen (idle/done state hub). */
  showDaily(): void;

  /**
   * Flip phase back to the board screen WITHOUT touching the engine/moves — used to
   * resume an in-progress game that the store STILL HOLDS in memory after SPA navigation
   * (daily → leaderboard → daily). Unlike `startDailyGame`, this performs NO reset/replay:
   * the live `engineState` + `moves` are reused as-is (owner-bug A 2026-06-28 «загрузка
   * доски дня при каждом заходе» — store-retained resume avoids reconstruction). Caller
   * MUST verify a live game is present first (see `resumeInProgress` same-game guard).
   */
  showBoard(): void;

  /**
   * Transition to the mode-selector screen (extra-modes, §5.1). The entry point
   * is the «Играть» (former casual) button — the player picks Предсказание /
   * Классика / Вслепую, then a card-click calls `POST /game/start` + startAllTimeGame.
   * No board is mounted here (C1: board-as-etalon untouched). Back → daily.
   */
  showModeSelect(): void;

  /**
   * Transition to the leaderboard screen (in-shell destination for the global
   * nav-rail + «К лидерборду» buttons). Two surfaces share `leaderboard-screen`:
   *   - daily board (default; tabs Сегодня / Вчера).
   *   - all-time per mode (extra-modes §5.4) — pass `allTimeMode` to land on a
   *     mode tab (Предсказание / Классика / Вслепую).
   */
  showLeaderboard(allTimeMode?: GameMode): void;

  /**
   * Transition to the settings screen (in-shell destination for the global nav-rail
   * «Настройки» item, brief-39 §C). Game-agnostic surface — does not touch engine
   * state; the user returns via the screen's «Назад» / the nav-rail.
   */
  showSettings(): void;

  // showWorkshop() УДАЛЁН вместе с «животными» (workshop-animals-removal.md): пункт
  // «Мастерская» теперь disabled (нет onSelect), экран зверей снят. Прямой /workshop
  // → graceful fallback на дефолт-поверхность (lib/game-routing isRetiredGamePath).

  /**
   * Transition to the gnomes plaque (плашка «Гномы» — meta-surface, мета «Гномы»
   * GN-1; in-shell destination for the global nav-rail «Гномы» item per
   * meta-gnomes.md §5.1). Game-agnostic surface — does not touch engine state.
   * UI-фаза: рендерит 7 карточек гномов из placeholder/snapshot (server gnomes
   * block — GN-4, after spec). Returns via the nav-rail.
   *
   * `focusColor` (optional) — deep-link фокус на конкретном помощнике (тап по
   * карточке в оверлее «Открыт новый помощник!»): экран скроллит и подсвечивает
   * именно эту карточку. Пишется в `gnomesFocusColor` (mirror `showLeaderboard`).
   */
  showGnomes(focusColor?: ColorIndex): void;

  /**
   * Transition to the profile hub (хаб-Профиль — meta-surface, Срез 4b; in-shell
   * destination for the global nav-rail «Профиль» item per meta-jeweler-surfaces.md
   * §6.1). Account showcase (уровень / ачивки / галерея / книга / статистика) —
   * reads `GET /meta/state` (+ progression) server-truth, display-only (I-1).
   */
  showProfile(): void;

  /**
   * Transition to the «Книга реплеев» screen (личный каталог реплеев — replay-
   * collection.md / ADR-0023; in-shell destination for the global nav-rail «Книга»
   * item). Account-bound surface: два альбома (Свои авто-∞ / Чужие ручные с ёмкостью),
   * вкладки по режимам; reads `GET /book/*` server-truth, открывает партию общим
   * replay-flow (gate §3A). Game-agnostic — does not touch engine state.
   */
  showBook(): void;

  /**
   * Transition to the «Поддержать» screen (крипто-донат L0, ADR-0024 addendum;
   * in-shell destination for the nav-rail «♥ Поддержать» item). Game-agnostic:
   * static crypto-receive cards (address + QR + copy) from `lib/donate-targets`.
   * No pay-to-win — server never learns about a donation (L0).
   */
  showSupport(): void;

  /**
   * Transition to a content page (лендинг / правила / гайд / стратегия / FAQ /
   * about) ВНУТРИ приложения — тот же контент, что краулер видит в статике
   * (ADR-0020 §«Сшить контент в приложение»). `lang`+`slug` ('' = лендинг) →
   * `contentTarget`, drives the app.ts `content` mount branch. Origin-relative
   * URL обновляется в app.ts (History API, контент-навигация НЕ отложена — Фаза 0-2).
   */
  showContent(lang: string, slug: string): void;

  /**
   * Set the active product language (ADR-0021 D-2 / T-I18N-2 AC-6). Updates `locale`
   * (single source-of-truth) and notifies subscribers — this IS the «reactive switch
   * without reload»: the game UI, content screen and the rail trigger re-render their
   * visible text on this state change. While on the `content` phase, `contentTarget.lang`
   * is synced to follow `locale` (it is DERIVED, not a second source). The engine
   * (engineState / moves / seed) is NOT touched — i18n is a presentation layer.
   * Catalog lazy-loading + URL-follow (navigateContent) live in the caller (rail
   * control), not here — the store only owns the language value + reactivity.
   */
  setLocale(lang: UiLang): void;

  /** Set the server submit result (called by board-screen after POST /finish-game). */
  setDailySubmitResult(result: SubmitResult | null): void;

  /**
   * Toggle the recovery-replay guard (board-feedback-animations.md §2.4 / AC-13).
   * `daily-board-screen.resumeInProgress` wraps its move-replay loop in
   * `setReplaying(true)` … `setReplaying(false)` so the live board renders the
   * recovered state STATICALLY (no spawn-pop, no line-clear overlay). See the
   * `replaying` field doc.
   */
  setReplaying(replaying: boolean): void;

  /** Reset to a fresh idle state (used between sessions). */
  reset(): void;
}

// =============================================================================
// Seed helper (T-5 brief: local Math.random fallback; ?seed query param wins)
// =============================================================================

function randomUint32(): number {
  return Math.floor(Math.random() * UINT32_MAX) >>> 0;
}

// =============================================================================
// Store factory
// =============================================================================

/**
 * Vanilla Zustand store (no React dep).
 *
 * Public API:
 *   - `gameStore.getState()` — read current state.
 *   - `gameStore.setState(partial)` — internal; UI code shouldn't call this.
 *   - `gameStore.subscribe(listener)` — react к state changes from screens.
 *
 * For compat with prior commit / smoke imports we also export `useGameStore`
 * as an alias (без React-style hook semantics — same StoreApi).
 */
export const gameStore: StoreApi<GameStoreState> = createStore<GameStoreState>((set, get) => ({
  phase: 'board',
  engineState: null,
  mode: 'predict',
  seed: 0,
  moves: [],
  context: CASUAL_CONTEXT,
  selectedCell: null,
  finishReason: null,
  dailySubmitResult: null,
  engineVersion: CURRENT_ENGINE_VERSION,
  lastInvalidReason: null,
  lastMoveEvents: null,
  replaying: false,
  replayShortId: null,
  leaderboardAllTimeMode: null,
  gnomesFocusColor: null,
  contentTarget: null,
  // Active product language — start at the `en` fallback (geo-default / persist is
  // T-I18N-3; single source-of-truth per ADR-0021 D-2). Not a magic literal.
  locale: DEFAULT_CONTENT_LANG,
  preStart: false,

  startNewGame(seed, mode = 'predict') {
    const useSeed = seed !== undefined ? seed >>> 0 : randomUint32();
    const st = startState(useSeed);
    set({
      phase: 'board',
      engineState: st,
      seed: useSeed,
      moves: [],
      context: CASUAL_CONTEXT,
      // Default predict (the common casual surface); the casual RESUME path passes
      // the cached run's original mode (RP-4) so the hint level is restored.
      mode,
      selectedCell: null,
      finishReason: null,
      dailySubmitResult: null,
      lastInvalidReason: null,
      lastMoveEvents: null,
      replaying: false,
      replayShortId: null,
      preStart: false,
    });
    // Reflect current seed in the URL so free-play is refresh-replayable (F5 → та же
    // доска) и shareable. replaceState — no history entry.
    // ⚠ owner-directive 2026-06-25: seed УБРАН из ВИДИМОГО UI (подзаголовок-текст снят).
    // НО он всё ещё ложится в адресную строку здесь — это load-bearing для refresh-replay
    // free-play. Убрать его из URL = менять модель free-play персистентности/роутинга
    // (refresh больше не воспроизведёт доску) → НЕ делаю точечно, ФЛАГ owner'у (см. PR).
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', `?seed=${useSeed}`);
    }
  },

  startDailyGame({ seed, dateOfDay }) {
    const useSeed = seed >>> 0;
    const st = startState(useSeed);
    set({
      phase: 'board',
      engineState: st,
      seed: useSeed,
      moves: [],
      context: { kind: 'daily', dateOfDay, seedFromServer: useSeed },
      // Per core-daily-board.md §3.4 — mode is locked к 'predict' для daily.
      mode: 'predict',
      selectedCell: null,
      finishReason: null,
      dailySubmitResult: null,
      lastInvalidReason: null,
      lastMoveEvents: null,
      // Reset the recovery guard on every fresh start; resumeInProgress sets it
      // back to `true` immediately AFTER this call, around the replay loop.
      replaying: false,
      replayShortId: null,
      preStart: false,
    });
    // Daily attempts don't expose seed в URL — daily seed is server-authoritative
    // and shouldn't be «discoverable» through URL hacking (concept §7 anti-cheat).
    if (typeof window !== 'undefined') {
      // Clear ?seed= если осталось от прошлой casual игры.
      window.history.replaceState({}, '', window.location.pathname);
    }
  },

  startAllTimeGame({ seed, sessionId, mode, preStart = false }) {
    const useSeed = seed >>> 0;
    const st = startState(useSeed);
    set({
      phase: 'board',
      engineState: st,
      seed: useSeed,
      moves: [],
      context: { kind: 'all_time', sessionId, seedFromServer: useSeed },
      // Mode controls ONLY `next`-visibility (predict: ghosts+niche; classic:
      // niche only; blind: nothing) — §2.1. The engine generates `next` always.
      mode,
      selectedCell: null,
      finishReason: null,
      dailySubmitResult: null,
      lastInvalidReason: null,
      lastMoveEvents: null,
      replaying: false,
      replayShortId: null,
      // lazy-on-preview (T-A): `preStart=true` mounts the ALREADY-SCORED session in
      // preview — «Начать» later flips it false (beginPreviewedGame) without a second
      // round-trip / reshuffle (AC-A.2). `preStart=false` → game is live immediately.
      preStart,
    });
    // Non-daily seed is server-authoritative (POST /game/start) — never exposed
    // in the URL (no ?seed= leak; anti-shadow §3.6).
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', window.location.pathname);
    }
  },

  beginPreviewedGame() {
    // «Начать» (T-A): pure client transition over the loaded server session. NO new
    // seed, NO board reshuffle, NO `POST /game/start` (the session is already
    // allocated — addendum Решение 1 / AC-A.2). No-op if not in preview.
    if (!get().preStart) return;
    set({ preStart: false });
  },

  selectCell(r, c) {
    const { engineState, selectedCell } = get();
    if (!engineState) return;
    if (get().finishReason) return; // board locked
    // Pre-start PREVIEW is not playable — клики не выделяют и не двигают (§5.2).
    if (get().preStart) return;

    // Click on a ball: select it (replacing any prior selection).
    // Clear lastMoveEvents so a re-render from re-selection can't reanimate the
    // previous clear (board-feedback-animations.md §2.3 — animation triggers ONLY
    // on a NEW move; selection is not a move).
    if (engineState.grid[r]![c] !== 0) {
      set({ selectedCell: { r, c }, lastInvalidReason: null, lastMoveEvents: null });
      return;
    }

    // Click on empty cell with no prior selection — nothing to do.
    if (!selectedCell) return;

    // Click on empty cell with selection — attempt move.
    get().attemptMove(r, c);
  },

  attemptMove(r, c) {
    const { engineState, selectedCell } = get();
    if (!engineState || !selectedCell) {
      return { ok: false, reason: 'nopath' };
    }
    // Pre-start PREVIEW records no moves — it's a non-scored layout preview (§5.2).
    if (get().preStart) return { ok: false, reason: 'nopath' };
    const { r: sr, c: sc } = selectedCell;

    // Engine state is mutated in place by applyMove (matches prototype semantics).
    const result = applyMove(engineState, sr, sc, r, c);

    if (!result.ok) {
      set({ lastInvalidReason: result.reason, lastMoveEvents: null });
      return result;
    }

    // Push the accepted move into history.
    const nextMoves = get().moves.slice();
    nextMoves.push({ sr, sc, r, c });
    set({
      moves: nextMoves,
      selectedCell: null,
      lastInvalidReason: null,
      // Per-move clear events (board-feedback-animations.md §2.3). `undefined`
      // (nothing cleared) → null so the board-screen renders no overlay.
      lastMoveEvents: result.events ?? null,
      // Engine state is the same reference — but trigger react by re-assigning.
      engineState,
    });

    return result;
  },

  endGame(reason) {
    set({ finishReason: reason, selectedCell: null });
  },

  showResult() {
    set({ phase: 'result' });
  },

  showReplay() {
    // Own last-game replay — clear any remote short_id so app.ts mounts last-game.
    set({ phase: 'replay', replayShortId: null });
  },

  showReplayById(shortId) {
    set({ phase: 'replay', replayShortId: shortId });
  },

  showStart() {
    set({ phase: 'start' });
  },

  showDaily() {
    set({ phase: 'daily' });
  },

  showBoard() {
    // Pure phase flip — engineState/moves/context untouched (store-retained resume,
    // owner-bug A 2026-06-28). No reset/replay: the in-memory live game is reused as-is.
    set({ phase: 'board' });
  },

  showModeSelect() {
    set({ phase: 'modeSelect' });
  },

  showLeaderboard(allTimeMode) {
    set({ phase: 'leaderboard', leaderboardAllTimeMode: allTimeMode ?? null });
  },

  showSettings() {
    set({ phase: 'settings' });
  },

  // showWorkshop() удалён вместе с «животными» (workshop-animals-removal.md).

  showGnomes(focusColor) {
    // Mirror showLeaderboard(allTimeMode): write the focus target into the store
    // payload; the gnomes screen consumes it once (sets back to null). Absent →
    // null (plain navigation lands at the top of the list, prior behaviour).
    set({ phase: 'gnomes', gnomesFocusColor: focusColor ?? null });
  },

  showProfile() {
    set({ phase: 'profile' });
  },

  showBook() {
    set({ phase: 'book' });
  },

  showSupport() {
    set({ phase: 'support' });
  },

  showContent(lang, slug) {
    set({ phase: 'content', contentTarget: { lang, slug } });
  },

  setLocale(lang) {
    // Single source-of-truth (ADR-0021 D-2). On the content phase, contentTarget.lang
    // is DERIVED from locale → keep it in sync so the content screen re-resolves the
    // same slug in the new language. The set() notifies subscribers (zustand) — that
    // IS the «reactive switch without reload». Engine untouched (presentation layer).
    const { phase, contentTarget } = get();
    if (phase === 'content' && contentTarget) {
      set({ locale: lang, contentTarget: { lang, slug: contentTarget.slug } });
    } else {
      set({ locale: lang });
    }
  },

  setDailySubmitResult(result) {
    set({ dailySubmitResult: result });
  },

  setReplaying(replaying) {
    set({ replaying });
  },

  reset() {
    set({
      phase: 'board',
      engineState: null,
      seed: 0,
      moves: [],
      context: CASUAL_CONTEXT,
      selectedCell: null,
      finishReason: null,
      dailySubmitResult: null,
      lastInvalidReason: null,
      lastMoveEvents: null,
      replaying: false,
      replayShortId: null,
      leaderboardAllTimeMode: null,
      gnomesFocusColor: null,
      preStart: false,
    });
  },
}));

/** Alias preserved для compat с прежним именованием (commit 1 skeleton). */
export const useGameStore = gameStore;

// =============================================================================
// Read-only selectors (UI uses these — never reach into engineState directly)
// =============================================================================

/** Live 9×9 grid (read-only view). Returns null if no game in progress. */
export function getGrid(s: GameStoreState): CellValue[][] | null {
  return s.engineState ? s.engineState.grid : null;
}

/** Live «next» balls (up to 3). Returns empty array if no game. */
export function getNext(s: GameStoreState): NextBall[] {
  return s.engineState ? s.engineState.next : [];
}

/** Live score. Mirrors `engineState.score`. */
export function getScore(s: GameStoreState): number {
  return s.engineState ? s.engineState.score : 0;
}

/** Live lines cleared. */
export function getLines(s: GameStoreState): number {
  return s.engineState ? s.engineState.lines : 0;
}

/** Count of empty cells — used by `BoardScreen` для dead-end / board-full detect. */
export function getEmptyCellCount(s: GameStoreState): number {
  if (!s.engineState) return N * N;
  let count = 0;
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      if (s.engineState.grid[r]![c] === 0) count++;
    }
  }
  return count;
}
